import { useEffect } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { CheckCircle2 } from 'lucide-react'

interface GapResolvedToastProps {
  /** Title of the gap that just resolved. `null` keeps the toast hidden. */
  resolvedGapTitle: string | null
  /** Notify parent the toast has timed out so state can clear. */
  onDismiss: () => void
  /** Auto-dismiss after this many ms. Defaults to 3500. */
  durationMs?: number
}

/**
 * Signature motion moment per DESIGN.md decision 2.1: gap-resolved toast.
 *
 * Slides up from the bottom-right when a gap is resolved. The accordion
 * collapse animation is handled by Radix on the row itself (since the page
 * removes the resolved id from the controlled `value` array). Respects
 * `prefers-reduced-motion` — fades only, no transform offset.
 */
export function GapResolvedToast({
  resolvedGapTitle,
  onDismiss,
  durationMs = 3500,
}: GapResolvedToastProps) {
  const reduce = useReducedMotion()

  useEffect(() => {
    if (!resolvedGapTitle) return
    const id = window.setTimeout(onDismiss, durationMs)
    return () => window.clearTimeout(id)
  }, [resolvedGapTitle, durationMs, onDismiss])

  return (
    <div
      aria-live="polite"
      role="status"
      data-testid="gap-resolved-toast-region"
      className="fixed bottom-6 right-6 z-50 pointer-events-none"
    >
      <AnimatePresence>
        {resolvedGapTitle && (
          <motion.div
            key={resolvedGapTitle}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            data-testid="gap-resolved-toast"
            className="pointer-events-auto flex items-center gap-3 rounded-lg border border-success/40 bg-bg-elevated px-4 py-3 shadow-lg max-w-sm"
          >
            <CheckCircle2 size={18} className="text-success shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-display font-semibold text-text-primary">
                Gap resolved
              </span>
              <span className="text-xs text-text-tertiary truncate">
                {resolvedGapTitle}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
